// Posterize — reduces the number of brightness levels per channel.
// Each channel is quantized to `levels` distinct values (2..255).
// Photoshop range is 2..255, default 4.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface PosterizeParams {
  levels: number; // 2..255, default 4
}

export const Posterize: Filter<PosterizeParams> = {
  id: "posterize",
  displayName: "Posterize",
  defaultParams: { levels: 4 },

  apply(input: PixelBuffer, params: PosterizeParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const levels = clampRange(params.levels, 2, 255) | 0;
    const step = 255 / (levels - 1);

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      dst[o] = q(src[o] ?? 0, step);
      dst[o + 1] = q(src[o + 1] ?? 0, step);
      dst[o + 2] = q(src[o + 2] ?? 0, step);
      dst[o + 3] = src[o + 3] ?? 0;
    }
    return out;
  },
};

function q(v: number, step: number): number {
  return Math.round(Math.round(v / step) * step);
}

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return lo;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
